import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchThreadCounts } from '../api/notifications';

/** What one record's badge needs: how many conversations, how many unread. */
export type ThreadCount = { total: number; unread: number };

/**
 * Conversation counts for a whole page of records, in one request
 * (COMMUNICATIONS-DESIGN H3).
 *
 * ⚠️ **One request per page, never one per row.** `<RecordConversations>`
 * without a `count` renders the package's self-fetching button, which is right
 * on a detail page and wrong on a list: forty rows is forty requests, and forty
 * more on every page change. That is not a hypothetical — the enquiries page
 * shipped that way once. Supplying `count` is what makes the button free until
 * it is clicked, and this is where the count comes from.
 *
 * ⚠️ **`undefined` and `null` are different answers, and callers must keep them
 * apart.** While this is loading it returns `undefined`, and a row should pass
 * `null` — "the page owns this badge and does not yet know the number".
 * Passing `undefined` through hands the row back to the self-fetching button,
 * which is the exact failure this hook exists to prevent; rendering a missing
 * count as `0` claims a conversation does not exist. `countFor` below is the
 * safe reader, so no caller has to remember.
 *
 * The id list is the query key, so paging refetches and re-rendering does not.
 */
export function useThreadCounts(entityType: string, entityIds: string[]) {
  // Sorted so two renders of the same page in a different order share a cache
  // entry rather than fetching twice.
  const key = [...entityIds].sort();

  const query = useQuery({
    queryKey: ['thread-counts', entityType, key],
    queryFn: () => fetchThreadCounts(entityType, key),
    enabled: key.length > 0,
  });

  /**
   * This record's count: the number when it is known, `null` while it is not.
   * Never `undefined`, so a row can never fall back to fetching for itself.
   *
   * ⚠️ **Stable across renders, keyed on the data.** A fresh closure each
   * render is not merely wasteful — a caller that memoises its columns
   * captures the *first* one, which was created before the query resolved and
   * therefore answers `null` forever. That is not a stale number, it is a
   * badge that never appears, and it shipped that way on two pages until
   * review caught it. `useCallback` makes the identity change exactly when
   * the answer does, so `[countFor]` in a dependency array is both correct
   * and cheap.
   */
  const countFor = useCallback(
    (entityId: string): ThreadCount | null =>
      query.data === undefined ? null : (query.data[entityId] ?? { total: 0, unread: 0 }),
    [query.data],
  );

  return { ...query, countFor };
}
